import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPopupModal = ({ type, onClose }) => {
  const [popup, setPopup] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [loading, setLoading] = useState(true);

  const storageKey = `login_popup_dismissed_${type}`;

  const handleDismiss = useCallback(() => {
    if (dontShowAgain && popup?.updated_at) {
      localStorage.setItem(storageKey, popup.updated_at);
    }
    onClose();
  }, [dontShowAgain, popup, storageKey, onClose]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [handleDismiss]);

  useEffect(() => {
    fetchPopup();
  }, [type]);

  const fetchPopup = async () => {
    try {
      const response = await axios.get(`${API}/public/login-popup/${type}`);
      
      if (!response.data.enabled) {
        onClose();
        return;
      }

      // Check if user dismissed this version
      const dismissedAt = localStorage.getItem(storageKey);
      if (dismissedAt && response.data.updated_at) {
        // If popup was updated after user dismissed, show again
        if (new Date(dismissedAt) >= new Date(response.data.updated_at)) {
          onClose();
          return;
        }
      }

      setPopup(response.data);
    } catch (error) {
      console.error('Failed to fetch login popup:', error);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (loading || !popup) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={handleDismiss}
    >
      <div 
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header accent */}
        <div className="h-1 bg-gradient-to-r from-amber-500 to-amber-600" />
        
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="p-8 text-center">
          {/* Title */}
          {popup.title && (
            <p className="text-amber-400 text-sm font-medium tracking-wide uppercase mb-2">
              {popup.title}
            </p>
          )}
          
          {/* Subtitle */}
          {popup.subtitle && (
            <h2 className="text-2xl font-bold text-white mb-4">
              {popup.subtitle}
            </h2>
          )}
          
          {/* Content */}
          {popup.content && (
            (() => {
              // Check if content contains HTML tags
              const hasHtmlTags = /<[a-z][\s\S]*>/i.test(popup.content);
              
              if (hasHtmlTags) {
                // Render as HTML
                return (
                  <div 
                    className="text-slate-400 text-sm leading-relaxed mb-6 text-left
                      [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:text-white
                      [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h2]:text-white
                      [&_p]:mb-2 [&_p]:text-slate-300
                      [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:mb-2
                      [&_ol]:list-decimal [&_ol]:ml-4 [&_ol]:mb-2
                      [&_li]:text-slate-300 [&_li]:mb-1
                      [&_b]:font-bold [&_strong]:font-bold
                      [&_i]:italic [&_em]:italic
                      [&_u]:underline
                      [&_font]:text-inherit
                      [&_div]:mb-2"
                    dangerouslySetInnerHTML={{ __html: popup.content }}
                  />
                );
              } else {
                // Render as plain text with line breaks
                return (
                  <p className="text-slate-400 text-sm leading-relaxed whitespace-pre-line mb-6">
                    {popup.content}
                  </p>
                );
              }
            })()
          )}
          
          {/* Button */}
          <Button
            onClick={handleDismiss}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8"
            data-testid="login-popup-dismiss"
          >
            {popup.button_text || 'Got it!'}
          </Button>
          
          {/* Don't show again checkbox */}
          <label className="mt-4 flex items-center justify-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500 focus:ring-offset-slate-900"
            />
            <span className="text-slate-500 text-xs">Don't show this again</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default LoginPopupModal;
